import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useInView } from 'react-intersection-observer'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { buildAccommodationCancellationPolicies } from '@luxuryescapes/lib-refunds'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import RadioInput from 'LuxKit/Radio/RadioInput'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import RestrictedCancellationPolicyTimeline from 'components/Common/CancellationPolicies/RestrictedCancellationPolicyTimeline'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import HelpPane from 'components/Common/HelpPane'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import InlineIcon from 'components/utils/InlineIcon'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import ModalContext from 'contexts/ModalContext'
import useRestrictedPropertyCancellationPolicy from 'hooks/useRestrictedPropertyCancellationPolicy'
import {
  formatHeadlineCancellationPolicyStartForDisplay,
  getBrowserTimeZone,
  intlDateFormatter,
} from 'lib/datetime/dateUtils'
import { isRestrictedPropertyPolicyEligibleRate } from 'lib/offer/cancellationPolicyUtils'

const StyledRadioInput = styled(RadioInput)`
  display: flex;
  align-items: start;
`

const CancellationClickable = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  text-decoration: none;

  &.locked {
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

interface CancellationDisplayText {
  primary: string
  secondary?: string
  highlightPrimary: boolean
}

interface CancellationTextOptions {
  intl: ReturnType<typeof useIntl>
  rate: App.EnrichedPropertyRate
  timezone: string
  dateFormattingLocale: string
  restrictedDeadline: Date | undefined
}

function getCancellationText({
  intl,
  rate,
  timezone,
  dateFormattingLocale,
  restrictedDeadline,
}: CancellationTextOptions): CancellationDisplayText {
  const { headlineCancellationPolicyType, headlineCancellationPolicyStart } = rate

  const nonRefundableLabel = intl.formatMessage({
    id: 'xvWHpDA1-Kplt1HmWGii',
    defaultMessage: 'Non refundable',
    description: 'Cancellation policy label for non-refundable property rates',
  })
  const freeCancellationLabel = intl.formatMessage({
    id: 'NmC8LTH4QQoMwIso1Xbh',
    defaultMessage: 'Free cancellation',
    description: 'Cancellation policy label for fully refundable property rates',
  })
  const partiallyRefundableLabel = intl.formatMessage({
    id: 'ZznnSheC7alEPdRGvXTi',
    defaultMessage: 'Partially refundable',
    description: 'Cancellation policy label for partially refundable property rates',
  })

  if (headlineCancellationPolicyType === 'nonRefundable') {
    return {
      primary: nonRefundableLabel,
      highlightPrimary: false,
    }
  }

  const refundText = headlineCancellationPolicyType === 'fullyRefundable'
    ? freeCancellationLabel
    : partiallyRefundableLabel

  const formattedDate = restrictedDeadline ?
      intlDateFormatter(restrictedDeadline, INTL_SHORT_DAY_NAME_DAY_MONTH, dateFormattingLocale, getBrowserTimeZone()) :
    headlineCancellationPolicyStart && formatHeadlineCancellationPolicyStartForDisplay(
      headlineCancellationPolicyStart,
      timezone,
      dateFormattingLocale,
    )

  if (formattedDate) {
    return {
      primary: refundText,
      secondary: intl.formatMessage(
        {
          id: 'hv1uXBUiZ3e8KSTtpNec',
          defaultMessage: 'until {date}',
          description: 'Date suffix for cancellation policy in the date selection or calendar picker',
        },
        { date: formattedDate },
      ),
      highlightPrimary: true,
    }
  }

  return {
    primary: refundText,
    highlightPrimary: true,
  }
}

interface Props {
  rate: App.EnrichedPropertyRate
  radioGroupName: string
  isSelected: boolean
  isLocked: boolean
  priceDifference: number
  staffRateLabel: string | undefined
  propertyTimezone: string
  dateFormattingLocale: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveUpgrade?: () => void
}

function PropertyRefundabilityRateOption(props: Props) {
  const {
    rate,
    radioGroupName,
    isSelected,
    isLocked,
    priceDifference,
    staffRateLabel,
    propertyTimezone,
    dateFormattingLocale,
    onChange,
    onRemoveUpgrade,
  } = props

  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const policyDescriptions = useMemo(() => buildAccommodationCancellationPolicies({
    fullRefundableUntil: rate.fullyRefundableUntil,
    cancellationPolicies: rate.cancellationPolicies.map(({
      startIsoDateTime,
      endIsoDateTime,
      penaltyPrice,
      isFullyNonRefundable,
      originalDescription,
    }) => ({
      amount: penaltyPrice,
      percent: 0,
      description: originalDescription,
      start: startIsoDateTime,
      end: endIsoDateTime,
      nonRefundable: isFullyNonRefundable,
    })),
  }, {
    timezone: propertyTimezone,
    currencyCode: rate.currency,
    hideExcludesFlights: !rate.includesFlights,
    locale: intl.locale,
  }).description, [rate, propertyTimezone, intl.locale])

  const restrictedPolicyEligible = isRestrictedPropertyPolicyEligibleRate(rate)
  const [activationRef, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
    skip: !restrictedPolicyEligible,
  })

  const headlineCancellationPolicyStart = useMemo(
    () => rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined,
    [rate.headlineCancellationPolicyStart],
  )

  const restrictedView = useRestrictedPropertyCancellationPolicy(
    headlineCancellationPolicyStart,
    propertyTimezone,
    restrictedPolicyEligible,
    policyDescriptions,
    { canActivate: inView },
  )

  const text = getCancellationText({
    intl,
    rate,
    timezone: propertyTimezone,
    dateFormattingLocale,
    restrictedDeadline: restrictedView?.refundToOriginalDeadlineDate,
  })

  const restrictedModalTitle = intl.formatMessage({
    id: 'BkJWDWHpgmKuIOdQMew3',
    defaultMessage: 'Cancellation policy',
    description: 'Modal title for the restricted cancellation policy timeline on a property rate option',
  })

  const showCancellationDetails = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    showModal(<Modal title={restrictedView ? restrictedModalTitle : text.primary}>
      {restrictedView && <RestrictedCancellationPolicyTimeline view={restrictedView} />}
      {restrictedView && <Hr size="md" />}
      {(restrictedView ? restrictedView.penaltyLines : policyDescriptions)
        .map((line, index) => <BodyText variant={restrictedView ? 'medium' : 'large'} as="p" key={index}>{line}</BodyText>)}
      {!restrictedView && <HelpPane />}
    </Modal>)
  }, [showModal, restrictedView, restrictedModalTitle, text.primary, policyDescriptions])

  return (
    <StyledRadioInput
      name={radioGroupName}
      value={rate.rateId}
      onChange={onChange}
      checked={isSelected}
      disabled={isLocked}
    >
      <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
        <BodyText
          variant="medium"
          weight={text.highlightPrimary ? 'bold' : undefined}
          colour={text.highlightPrimary ? 'highlight-primary-dark' : (isLocked ? 'normal-contrast' : undefined)}
          wrap="no-wrap"
        >
          {text.primary}
        </BodyText>
        {text.secondary && (
          <BodyText variant="medium" colour={isLocked ? 'normal-contrast' : undefined} wrap="no-wrap">
            {text.secondary}
          </BodyText>
        )}
        {staffRateLabel && (
          <BodyText variant="medium" colour="light-foreground-alt">
            {staffRateLabel}
          </BodyText>
        )}
        {!isSelected && priceDifference > 0 && (
          <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
            <FormattedMessage
              id="mGHDJoYlHarKV4E9f2Ln"
              defaultMessage="+{price}"
              description="Positive price difference with plus sign for refundability option"
              values={{ price: <FormatCurrency value={priceDifference} format="roundedDollar" /> }}
            />
          </BodyText>
        )}
        {!isSelected && priceDifference < 0 && (
          <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
            <FormatCurrency value={priceDifference} format="roundedDollar" />
          </BodyText>
        )}
        <CancellationClickable
          className={isLocked ? 'locked' : undefined}
          onClick={showCancellationDetails}
          ref={restrictedPolicyEligible ? activationRef : undefined}
        >
          <InlineIcon Icon={LineInfoCircleIcon} />
        </CancellationClickable>
        {isLocked && <BodyText variant="medium" colour="normal-contrast">
          <FormattedMessage
            id="qkpK0JdLZD8sOuc8qyhx"
            defaultMessage="(Unavailable with free upgrade. <link>Remove upgrade</link>)"
            description="Note on a cancellation option that the applied loyalty free package upgrade is not offered on, with a link to remove the upgrade"
            values={{
              link: (chunks) => <TextLink size="medium" colour="normal-contrast" underline onClick={onRemoveUpgrade}>{chunks}</TextLink>,
            }}
          />
        </BodyText>}
      </Group>
    </StyledRadioInput>
  )
}

export default PropertyRefundabilityRateOption
